import { useEffect, useRef, useState } from 'react';
import { Crop, PixelCrop } from 'react-image-crop';
import { Image } from '../../hooks/useImageUploader';
import { imgPreview } from '../utils';

interface Props {
  onClose: () => void;
  image: Image | null;
  handleSaveCrop: (crop: Crop | undefined, croppedObjectUrl: string | undefined) => void;
}

export const useCropImage = ({ image, onClose, handleSaveCrop }: Props) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string | null>(null);

  const [crop, setCrop] = useState<Crop>();
  const [aspectRatio, setAspectRatio] = useState<number>(1.91);

  const onChange = (newCrop: Crop) => {
    setCrop({ ...newCrop, aspect: aspectRatio });
  };

  const resetCrop = () => {
    setCrop(undefined);
  };

  const onImageLoaded = (img: HTMLImageElement) => {
    imgRef.current = img;
  };

  const handleSave = async () => {
    handleClose();

    const blob = await imgPreview(imgRef.current, crop as PixelCrop);
    if (!blob) return;

    const croppedObjectUrl = URL.createObjectURL(blob);
    handleSaveCrop(crop, croppedObjectUrl);
  };

  const handleDelete = () => {
    handleClose();

    handleSaveCrop(undefined, undefined);
  };

  const handleClose = () => {
    onClose();
    resetCrop();
    setOriginalImageUrl(null);
  };

  useEffect(() => {
    resetCrop();
  }, [aspectRatio]);

  useEffect(() => {
    if (!image) return;

    setOriginalImageUrl(URL.createObjectURL(image.file));
    setCrop(image.crop);
  }, [image]);

  return {
    originalImageUrl,
    crop,
    aspectRatio,
    onChange,
    onImageLoaded,
    setAspectRatio,
    handleSave,
    handleDelete,
    handleClose
  };
};
